import { useState } from "react";
import { Row, Col, Table, Button, UncontrolledTooltip } from "reactstrap";
import Select from "react-select";

import DeleteButton from "../../../components/Buttons/DeleteButton";

const EMPTY_FORM = { type: "", name: "", link: "", groups: [] };

// Mesma exigência do backend (@IsUrl({ require_protocol: true })): sem o
// protocolo o portal montaria um link relativo e quebrado.
const LINK_PATTERN = /^https?:\/\/\S+$/i;

// O tipo é a chave do link no portal. Mesma regra do
// @Matches(PORTAL_EVENT_LINK_TYPE_PATTERN) na admin-api.
const TYPE_PATTERN = /^[a-z0-9]+$/;

// A API converte o tipo para minúsculas; normalizar aqui deixa a tela igual ao
// que é salvo.
const normalizeType = (type) => type.trim().toLowerCase();

export default function ConfigEventLinks({ links, setLinks, groups }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingIndex, setEditingIndex] = useState(null);
  const [errors, setErrors] = useState({});

  const isEditing = editingIndex !== null;

  const groupOptions = groups.map((group) => ({
    label: group.name,
    value: group.id,
  }));

  const optionsOfIds = (ids) =>
    ids.map(
      (id) => groupOptions.find((option) => option.value === id) || { label: id, value: id }
    );

  // Sem grupo selecionado o link vale para todos — é o padrão do cadastro.
  const groupNamesOf = (ids) =>
    ids.length === 0
      ? "Todos"
      : optionsOfIds(ids)
          .map((option) => option.label)
          .join(", ");

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingIndex(null);
    setErrors({});
  };

  const validate = () => {
    const newErrors = {};
    const type = normalizeType(form.type);

    if (type && !TYPE_PATTERN.test(type)) {
      newErrors.type =
        "O tipo aceita apenas letras minúsculas e números, sem espaços ou caracteres especiais";
    }

    if (form.link.trim() && !LINK_PATTERN.test(form.link.trim())) {
      newErrors.link = "Informe o link completo, começando com http:// ou https://";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const newLink = {
      type: normalizeType(form.type),
      name: form.name.trim(),
      link: form.link.trim(),
      groups: form.groups,
    };

    if (isEditing) {
      setLinks(links.map((item, index) => (index === editingIndex ? newLink : item)));
    } else {
      setLinks([...links, newLink]);
    }

    resetForm();
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setForm({ ...links[index], groups: links[index].groups || [] });
    setErrors({});
  };

  const handleDelete = (index) => {
    setLinks(links.filter((item, itemIndex) => itemIndex !== index));

    if (editingIndex === index) {
      resetForm();
    } else if (isEditing && editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
  };

  return (
    <div style={{ marginTop: 20 }}>
      <label className="form-label" style={{ display: "flex" }}>
        Links{" "}
        <div id="tooltipLinks" style={{ paddingRight: "7px", height: "16px" }}>
          <div className="show-tips">
            <span>?</span>
          </div>
          <UncontrolledTooltip
            style={{ backgroundColor: "#6f42c1" }}
            placement="right"
            target="tooltipLinks"
          >
            O tipo é a chave do link no portal (ex.: wordpress): só letras
            minúsculas e números, sem espaços. Sem grupo selecionado, o link
            aparece para todos os participantes. Os links são gravados ao salvar
            a configuração.
          </UncontrolledTooltip>
        </div>
      </label>

      <Row>
        <Col md={6}>
          <input
            className="form-control"
            type="text"
            placeholder="Tipo"
            aria-label="Tipo do link"
            value={form.type}
            onChange={(e) => setField("type", e.target.value)}
          />
          {errors.type && <a style={{ color: "red" }}>{errors.type}</a>}
        </Col>
        <Col md={6}>
          <input
            className="form-control"
            type="text"
            placeholder="Nome"
            aria-label="Nome do link"
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
          />
          {errors.name && <a style={{ color: "red" }}>{errors.name}</a>}
        </Col>
      </Row>

      <Row style={{ marginTop: 10 }}>
        <Col md={12}>
          <input
            className="form-control"
            type="url"
            placeholder="https://exemplo.com.br"
            aria-label="Endereço do link"
            value={form.link}
            onChange={(e) => setField("link", e.target.value)}
          />
          {errors.link && <a style={{ color: "red" }}>{errors.link}</a>}
        </Col>
      </Row>

      <Row style={{ marginTop: 10 }}>
        <Col md={12}>
          <Select
            placeholder={"Grupos (vazio = todos)"}
            aria-label="Grupos que enxergam o link"
            isMulti
            value={optionsOfIds(form.groups)}
            onChange={(selected) =>
              setField("groups", (selected || []).map((option) => option.value))
            }
            noOptionsMessage={() => "Nenhuma opção encontrada"}
            options={groupOptions}
            classNamePrefix="select2-selection"
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (provided) => ({ ...provided, maxHeight: 150 }),
              menuList: (provided) => ({
                ...provided,
                maxHeight: 150,
                overflowY: "auto",
              }),
            }}
          />
        </Col>
      </Row>

      <Row style={{ marginTop: 10 }}>
        <Col md={12} style={{ display: "flex", justifyContent: "flex-end" }}>
          {isEditing && (
            <Button
              color="light"
              className="btn waves-effect waves-light"
              style={{ marginRight: 10 }}
              onClick={resetForm}
            >
              Cancelar
            </Button>
          )}
          <Button
            color="success"
            className="btn waves-effect waves-light"
            onClick={handleSubmit}
          >
            {isEditing ? "Salvar link" : "Adicionar link"}
          </Button>
        </Col>
      </Row>

      {links.length > 0 && (
        <div className="table-responsive" style={{ marginTop: 10 }}>
          <Table className="table mb-0">
            <thead className="table-light">
              <tr>
                <th>Tipo</th>
                <th>Nome</th>
                <th>Link</th>
                <th>Grupos</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {links.map((item, index) => (
                <tr key={index}>
                  <td>{item.type}</td>
                  <td>{item.name}</td>
                  <td style={{ wordBreak: "break-all" }}>{item.link}</td>
                  <td>{groupNamesOf(item.groups || [])}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <i
                      className="mdi mdi-pencil"
                      title="Editar"
                      style={{ color: "green", fontSize: 17, cursor: "pointer" }}
                      onClick={() => handleEdit(index)}
                    />
                    <DeleteButton onClick={() => handleDelete(index)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}
